/**
 * Multi-language support - Translations for UI
 */

export const translations = {
  en: {
    // Common
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    back: "Back",
    next: "Next",
    done: "Done",
    error: "Error",
    success: "Success",
    
    // View Page
    view: {
      shareTitle: "Share",
      copyLink: "Copy Link",
      linkCopied: "Link copied!",
      invalidTag: "Invalid Tag",
      invalidTagDesc: "This NFC tag code doesn't exist. Please scan a valid tag or contact the provider.",
      expired: "This content has expired",
      expiredDesc: "The content linked to this tag is no longer available. It was set to expire and the viewing period has ended.",
      expiredOn: "Expired on",
      comingSoon: "Coming Soon!",
      comingSoonDesc: "This content is not yet available. It has been scheduled to go live at a later time.",
      available: "Available",
      emptyTag: "Ready for Your Content!",
      emptyTagDesc: "This tag is waiting for a personal message. The gift giver can add photos, videos, and messages by scanning the NFC tag or using the upload link.",
    },
    
    // Upload Page
    upload: {
      title: "Upload Content",
      subtitle: "Add messages, photos, and videos to create your digital memory.",
      tagLocked: "Tag Locked",
      tagLockedDesc: "This tag already has content. Reset it first to add new content.",
      backgroundColor: "Background Color",
      backgroundColorDesc: "Optional - choose a color for your page",
      expirationDate: "Expiration Date",
      expirationDateDesc: "Optional - set when this content should expire and become unavailable",
      scheduleContent: "Schedule Content",
      scheduleContentDesc: "Optional - set when this content should become visible (for time-limited campaigns)",
      scanNotifications: "Scan Notifications",
      notifyDesc: "Get notified by email whenever someone scans this tag",
      titleLabel: "Title",
      titlePlaceholder: "e.g., Happy Birthday, Congratulations, Best Wishes...",
      messageLabel: "Message",
      messagePlaceholder: "Write your message here...",
      generateWithAI: "Generate with AI",
      media: "Media",
      uploadContent: "Upload your content",
      photoGallery: "Photo Gallery",
      videoGallery: "Video Gallery",
      takePhoto: "Take Photo",
      recordVideo: "Record Video",
      audioFile: "Audio File",
      audioMessage: "Audio Message",
      dragToReorder: "Drag photos to reorder. Click to view fullscreen.",
      previewCount: "Preview",
      saveContent: "Save Content",
      saving: "Saving...",
      preview: "Preview",
      getQRCode: "Get QR Code",
      getQR: "Get QR",
      useTemplate: "Use Template",
      expires: "Expires",
      goesLive: "Goes live",
      invalidTag: "Invalid Tag",
      invalidTagDesc: "This NFC tag code doesn't exist. Please scan a valid tag or contact the provider.",
      aiWriter: "AI Message Writer",
      aiWriterDesc: "Add optional context to help AI write a more personalized message for your",
      aiContextPlaceholder: "e.g., For my best friend who loves adventure...",
      generate: "Generate",
      generating: "Generating...",
      specialOccasion: "special occasion",
      templateApplied: "Template applied",
      photosReordered: "Photos reordered",
      mediaRemoved: "Media removed",
      aiGenerated: "AI message generated!",
      qrDownloaded: "QR code downloaded!",
      contentSaved: "Content saved successfully",
      compressing: "Compressing image...",
      uploading: "Uploading",
      processing: "Processing video... (compressing on server)",
      uploadingVideo: "Uploading video...",
      videoProcessing: "Video will be converted for all browsers",
      videoOptimized: "",
      compressed: "Compressed",
      fileTooLarge: "File size exceeds 100MB limit",
      uploadFailed: "Failed to upload file",
      uploadSuccess: "File uploaded successfully",
      saveFailed: "Failed to save content",
      aiFailed: "Failed to generate message",
      qrFailed: "Failed to download QR code",
      compressionFailed: "Could not compress image, uploading original",
      // Drag & Drop
      dragDrop: "Drag & drop files here",
      dropHere: "Drop files here!",
      dragDropDesc: "or use the buttons below to select files",
      invalidFileType: "Please drop image, video, or audio files",
      // Live Preview
      showLivePreview: "Live Preview",
      hideLivePreview: "Hide Preview",
      livePreviewTitle: "Live Preview",
      livePreviewEmpty: "Start adding content to see a live preview",
      livePreviewHint: "This preview updates as you type",
    },
    
    // Admin
    admin: {
      dashboard: "Admin Dashboard",
      manageDesc: "Manage your NFC tags, generate UUIDs, and create QR codes",
      totalTags: "Total Tags",
      withContent: "With Content",
      totalViews: "Total Views",
      uniqueVisitors: "Unique Visitors",
      viewsLast7Days: "Views - Last 7 Days",
      allTags: "All Tags",
      batches: "Batches",
      uuidGenerator: "UUID Generator",
      customTag: "Custom Tag",
      bulkUpload: "Bulk Upload",
      search: "Search...",
      selectAll: "Select all",
      quickSelect: "Quick Select",
      refresh: "Refresh",
      whiteLabel: "White-Label",
    },
    
    // Templates
    templates: {
      title: "Tag Templates",
      subtitle: "Quick start with pre-designed templates",
      wedding: "Wedding",
      weddingDesc: "Share your special day with loved ones",
      birthday: "Birthday",
      birthdayDesc: "Celebrate with a personalized message",
      business: "Business Card",
      businessDesc: "Professional contact information",
      product: "Product Info",
      productDesc: "Share product details and instructions",
      event: "Event",
      eventDesc: "Event details and RSVP information",
      memorial: "Memorial",
      memorialDesc: "Honor and remember loved ones",
    },

    // Marketing - Homepage
    marketing: {
      nav: {
        features: "Features",
        solutions: "Solutions",
        pricing: "Pricing",
        about: "About",
        faq: "FAQ",
        contact: "Contact",
        login: "Login",
        getStarted: "Get Started",
        language: "Language",
      },
      hero: {
        badge: "Bridge Physical to Digital",
        title: "Turn Any Object Into an",
        titleHighlight: "Interactive Experience",
        titleEnd: "",
        subtitle: "NFC tags and QR codes that connect physical products to digital content. Track engagement, update content instantly, and create unforgettable moments.",
        requestDemo: "Request Demo",
        seeHowItWorks: "See How It Works",
        trustedBy: "Trusted by 500+ businesses",
      },
      trustedBrands: "TRUSTED BY LEADING BRANDS",
      features: {
        title: "Everything You Need to Connect",
        subtitle: "Powerful tools to create, manage, and track your NFC and QR experiences",
        dualTech: "Dual Technology",
        dualTechDesc: "Every tag gets both NFC and QR code. Use together or separately for maximum reach.",
        instantUpdates: "Instant Updates",
        instantUpdatesDesc: "Change content anytime without reprinting. Reset and reuse for new campaigns.",
        realTimeAnalytics: "Real-Time Analytics",
        realTimeAnalyticsDesc: "Track every scan with location, device, and time data. Know your audience.",
        whiteLabel: "White-Label Ready",
        whiteLabelDesc: "Your brand, your platform. Fully customizable for agencies and enterprises.",
        multiLanguage: "Multi-Language",
        multiLanguageDesc: "Support for 7+ languages. Reach global audiences effortlessly.",
        aiPowered: "AI-Powered Content",
        aiPoweredDesc: "Let AI help write engaging messages. Create compelling content in seconds.",
      },
      solutions: {
        title: "Solutions for Every Need",
        subtitle: "Two powerful platforms, infinite possibilities",
        forBusiness: "For Businesses",
        forBusinessDesc: "Enterprise-grade NFC & QR management platform with analytics, batch operations, and white-label capabilities.",
        businessFeatures: [
          "Manage thousands of tags at scale",
          "Real-time analytics dashboard",
          "White-label for your brand",
          "API access & integrations",
          "Multi-tenant architecture"
        ],
        perfectFor: "Perfect for:",
        businessTags: ["Marketing Agencies", "Retail Brands", "Event Companies", "Manufacturers", "Packaged Goods", "Entertainment", "Sports"],
        giftsAwards: "Gifts & Awards",
        giftsAwardsDesc: "Transform gifts and trophies into magical keepsakes. Video messages, memories, and moments that last forever.",
        giftFeatures: [
          "Smart greeting cards with video",
          "Trophies that replay victory moments",
          "Wedding favors with memories",
          "Corporate awards with messages",
          "Memorial keepsakes"
        ],
        viewProducts: "View Products & Pricing",
      },
      howItWorks: {
        title: "How It Works",
        subtitle: "Three simple steps to create interactive experiences",
        step1: "Create Your Tag",
        step1Desc: "Generate unique NFC tags and QR codes in seconds. Customize colors and branding.",
        step2: "Add Content",
        step2Desc: "Upload photos, videos, and messages. Use AI to help write compelling content.",
        step3: "Track & Engage",
        step3Desc: "Monitor scans in real-time. See analytics, locations, and engagement metrics.",
      },
      pricing: {
        title: "Simple, Transparent Pricing",
        subtitle: "Start free, scale as you grow",
        starter: "Starter",
        starterDesc: "Perfect for small businesses",
        starterFeatures: ["Up to 100 tags", "Basic analytics", "QR code generator", "Email support"],
        professional: "Professional",
        professionalDesc: "For growing businesses",
        professionalFeatures: ["Up to 1,000 tags", "Advanced analytics", "Custom QR branding", "API access", "Priority support", "Team collaboration"],
        enterprise: "Enterprise",
        enterpriseDesc: "For large organizations",
        enterpriseFeatures: ["Unlimited tags", "White-label platform", "Dedicated support", "Custom integrations", "SLA guarantee", "On-boarding assistance"],
        mostPopular: "Most Popular",
        startFreeTrial: "Start Free Trial",
        contactSales: "Contact Sales",
        custom: "Custom",
        month: "/month",
      },
      testimonials: {
        title: "Loved by Businesses",
        testimonial1: "ScanIt2Go transformed how we engage customers. The analytics alone have been game-changing for our retail strategy.",
        testimonial1Author: "Sarah Johnson",
        testimonial1Role: "Marketing Director",
        testimonial1Company: "Fashion Retail Co",
        testimonial2: "We added NFC tags to our awards. Winners can now relive their victory moment anytime. It's magical.",
        testimonial2Author: "Michael Chen",
        testimonial2Role: "Event Manager",
        testimonial2Company: "Sports Awards Inc",
        testimonial3: "The white-label feature let us offer this to our clients under our brand. Revenue stream we didn't expect!",
        testimonial3Author: "Emma Williams",
        testimonial3Role: "Agency Owner",
        testimonial3Company: "Digital First Agency",
      },
      cta: {
        title: "Ready to Get Started?",
        subtitle: "Join 500+ businesses creating interactive experiences with ScanIt2Go",
        requestDemo: "Request Demo",
        viewPricing: "View Pricing",
      },
      contact: {
        title: "Get in Touch",
        subtitle: "Ready to transform your physical products into interactive experiences? Let's talk about your needs.",
        website: "Website",
        email: "Email",
        phone: "Phone",
        firstName: "First Name",
        lastName: "Last Name",
        emailLabel: "Email",
        company: "Company",
        message: "Message",
        messagePlaceholder: "Tell us about your project...",
        sendMessage: "Send Message",
        sending: "Sending...",
        captcha: "Security Check",
        newQuestion: "New question",
      },
      footer: {
        tagline: "Bridge physical to digital. Create unforgettable experiences.",
        product: "Product",
        features: "Features",
        pricing: "Pricing",
        solutions: "Solutions",
        dashboard: "Dashboard",
        company: "Company",
        aboutUs: "About Us",
        faq: "FAQ",
        contact: "Contact",
        privacyPolicy: "Privacy Policy",
        termsOfService: "Terms of Service",
        allRightsReserved: "All rights reserved.",
      },
    },

    // About Page
    about: {
      badge: "Our Story",
      title: "Bridging Physical and Digital Worlds",
      subtitle: "We started with a simple idea: physical objects should tell stories. Today, we help businesses and individuals create meaningful connections.",
      ourMission: "Our Mission",
      missionText: "We believe everyday objects can become powerful communication tools. Our mission is to provide intuitive, reliable, and beautiful technology that bridges physical products with digital experiences.",
      stats: {
        tagsCreated: "Tags Created",
        businessesServed: "Businesses Served",
        countriesReached: "Countries Reached",
        uptime: "Uptime",
      },
      ourVision: "Our Vision",
      visionText: "We envision a future where the line between physical and digital disappears. Where every product can tell a story, every gift can carry a unique experience, and every business can connect with customers in meaningful ways.",
      visionPoints: [
        "Universal NFC & QR standards for global reach",
        "Enterprise-grade security for all industries",
        "Simplified user experience for maximum accessibility",
        "Sustainable innovation for continuous product improvement"
      ],
      ourValues: "Our Values",
      values: {
        innovation: "Innovation",
        innovationDesc: "We constantly push the boundaries of technology.",
        customerFocus: "Customer Focus",
        customerFocusDesc: "Every decision is made with customer needs in mind.",
        passion: "Passion",
        passionDesc: "We believe in the power of technology to connect lives.",
        simplicity: "Simplicity",
        simplicityDesc: "We make complex technology accessible to everyone.",
      },
      ourJourney: "Our Journey",
      journey: {
        year2023: "2023",
        event2023: "Founded",
        desc2023: "Our journey began with a small team in the UK.",
        year2024: "2024",
        event2024: "Product Launch",
        desc2024: "Officially launched the platform, serving our first business customers.",
        year2025: "2025",
        event2025: "Global Expansion",
        desc2025: "Expanded to serve 500+ businesses from around the world.",
      },
      team: {
        title: "Meet Our Team",
        subtitle: "The passionate people behind ScanIt2Go",
        founder: "Founder & CEO",
        founderDesc: "Visionary with 15+ years of technology and marketing experience.",
      },
      ctaTitle: "Be Part of Our Story",
      ctaSubtitle: "Join thousands of businesses and individuals who are helping us shape the future of connected experiences.",
      getStarted: "Get Started Today",
      contactUs: "Contact Us",
    },

    // FAQ Page
    faq: {
      badge: "Help Center",
      title: "Frequently Asked Questions",
      subtitle: "Everything you need to know about ScanIt2Go. Can't find what you're looking for? Contact our team.",
      searchPlaceholder: "Search questions...",
      categories: {
        all: "All",
        general: "General",
        technical: "Technical",
        pricing: "Pricing & Plans",
        business: "Business Solutions",
        gifts: "Gifts & Awards",
      },
      questions: {
        q1: "What is ScanIt2Go?",
        a1: "ScanIt2Go is an NFC and QR code management platform that connects physical products to digital content. It allows you to create smart tags, manage content, track engagement, and create connected experiences.",
        q2: "How do NFC and QR codes work?",
        a2: "NFC (Near Field Communication) tags are triggered when a smartphone is brought close, while QR codes are scanned with a camera. Both technologies direct users to digital content. We provide both NFC and QR for maximum compatibility.",
        q3: "Does it work with any smartphone?",
        a3: "QR codes work with any smartphone with a camera. NFC is supported by most modern smartphones (iPhone 7+, most Android devices). We provide both NFC and QR options for wider compatibility.",
        q4: "Can I change the content after publishing?",
        a4: "Yes! That's one of our biggest advantages. You can update content anytime without changing the physical tag. Perfect for promotions, seasonal messages, or ongoing campaigns.",
        q5: "Is the content secure?",
        a5: "Absolutely. We use secure hosting, enterprise infrastructure, and GDPR-compliant data handling. Additional security features are available for enterprise customers.",
        q6: "What types of content can I upload?",
        a6: "You can upload text, images, videos, and audio files. Our platform supports most common formats and allows up to 100MB per file for multimedia content.",
        q7: "How does your pricing work?",
        a7: "We offer tiered plans for different needs: Starter (up to 100 tags), Professional (up to 1,000 tags), and Enterprise (unlimited). Each plan includes analytics, QR generation, and support.",
        q8: "Is there a free trial?",
        a8: "Yes! All paid plans include a 14-day free trial with full feature access. No credit card required.",
        q9: "Can I get custom pricing?",
        a9: "Absolutely. For enterprise customers and high-volume needs, we offer custom pricing based on your unique requirements. Contact our sales team.",
        q10: "What is white-labeling?",
        a10: "White-labeling allows you to present our platform with your own branding, colors, and logo. Perfect for agencies and businesses wanting to offer their own NFC/QR services.",
        q11: "Can I create accounts for my clients?",
        a11: "Yes! Our multi-tenant system allows you to create sub-accounts, manage permissions, and track separate billing for each client.",
        q12: "What kind of analytics do you track?",
        a12: "We track scan counts, unique visitors, geographic location, device types, browser information, and timestamps. Businesses can truly understand their audience.",
        q13: "How do the gift products work?",
        a13: "Our gift products (cards, awards, trophies) come with embedded NFC tags. You upload a video or message, and when the recipient scans the gift, they see your content. Perfect for special celebrations.",
        q14: "What products are available for gifts?",
        a14: "We offer NFC-enabled greeting cards, crystal awards, trophies, plaques, keychains, and various accessories. All products can be customized with specific printing designs.",
        q15: "Can I set up gift content in advance?",
        a15: "Yes! We support content scheduling. Set content to go live at a specific date/time - it will activate for an event, celebration, or announcement.",
        q16: "How long does my gift message stay?",
        a16: "Content linked to gifts is permanent. It remains accessible indefinitely unless you choose to remove it. You can also set optional expiration dates.",
        q17: "Will I be notified when someone views my gift?",
        a17: "Yes! Optional notifications alert you when your gift is first viewed - you'll know when your message has been experienced.",
        q18: "Is there a discount for bulk orders?",
        a18: "Absolutely! We offer volume discounts for 10+, 50+, and 100+ unit orders. Contact our sales team for custom pricing.",
      },
      stillHaveQuestions: "Still Have Questions?",
      stillHaveQuestionsDesc: "Can't find what you're looking for? Our team is happy to help.",
      emailUs: "Email Us",
      contactSupport: "Contact Support",
    },

    // Gifts Pricing Page
    giftsPricing: {
      badge: "Gifts & Awards",
      title: "Make Your Gifts Unforgettable",
      subtitle: "Special products that come alive with video messages, photos, and memories. Simple and beautiful.",
      howItWorks: "How It Works",
      step1: "Choose Your Product",
      step1Desc: "Select from our range of NFC and QR-enabled products",
      step2: "Upload Your Message",
      step2Desc: "Add video, photos, or text through your personal upload page",
      step3: "Share & Surprise",
      step3Desc: "Recipient experiences your moments with a tap or scan",
      products: {
        title: "Our Products",
        subtitle: "Every product comes with embedded NFC tags and QR codes",
        greetingCards: "Smart Greeting Cards",
        greetingCardsDesc: "Beautiful cards that come alive with video messages and photos. Perfect for birthdays, weddings, graduations, and special occasions.",
        greetingCardsFeature1: "Embedded NFC tag + QR code",
        greetingCardsFeature2: "Premium card stock",
        greetingCardsFeature3: "Supports video and photos",
        greetingCardsFeature4: "Unlimited content updates",
        singleCard: "Single Card",
        pack3: "3-Pack",
        pack10: "10-Pack",
        metalSurface: "+$1 for metal surface",
        awards: "Crystal Awards",
        awardsDesc: "Elegant crystal awards with embedded video memories. Ideal for corporate recognition, sports achievements, and special honors that replay victory moments.",
        awardsFeature1: "Premium crystal material",
        awardsFeature2: "Embedded NFC + QR",
        awardsFeature3: "Custom engraving options",
        awardsFeature4: "Luxury presentation box included",
        small: "Small (10cm)",
        medium: "Medium (15cm)",
        large: "Large (20cm)",
        trophies: "Video Trophies",
        trophiesDesc: "Just like a crown, but with memories. Perfect for teams and achievements.",
        trophiesFeature1: "Durable metal construction",
        trophiesFeature2: "NFC tag in base",
        trophiesFeature3: "Custom engraving available",
        trophiesFeature4: "All popular sizes",
        plaques: "Memorial Plaques",
        plaquesDesc: "Honor loved ones with videos, photos, and shared memories. Lasting tributes crafted with sensitive materials.",
        plaquesFeature1: "Weather-resistant materials",
        plaquesFeature2: "Embedded NFC technology",
        plaquesFeature3: "Photo engraving available",
        plaquesFeature4: "Indoor and outdoor use",
        memorialSmall: "Memorial Small",
        memorialLarge: "Memorial Large",
      },
      bulkOrders: {
        title: "Bulk Orders & Customization",
        subtitle: "Discounted pricing for businesses and events",
        weddingFavors: "Wedding Favors",
        weddingFavorsDesc: "Personalized NFC cards for guests. Each can link to unique content.",
        corporateGifts: "Corporate Gifts",
        corporateGiftsDesc: "Branded awards and recognition items with video messages.",
        eventMemorabilia: "Event Memorabilia",
        eventMemorabilitaDesc: "NFC-enabled products for concerts, conferences, and special events.",
        contactForPricing: "Contact for Pricing",
      },
      orderNow: "Order Now",
      contactSales: "Contact Sales",
      questions: "Have Questions?",
      questionsText: "Our team is happy to help. Reach out for bulk orders and custom requirements.",
      emailUs: "Email Us",
    },
  },
  
  es: {
    // Common
    loading: "Cargando...",
    save: "Guardar",
    cancel: "Cancelar",
    delete: "Eliminar",
    edit: "Editar",
    back: "Atrás",
    next: "Siguiente",
    done: "Hecho",
    error: "Error",
    success: "Éxito",
    
    // View Page
    view: {
      shareTitle: "Compartir",
      copyLink: "Copiar enlace",
      linkCopied: "¡Enlace copiado!",
      invalidTag: "Etiqueta inválida",
      invalidTagDesc: "Este código de etiqueta NFC no existe. Por favor escanea una etiqueta válida.",
      expired: "Este contenido ha expirado",
      expiredDesc: "El contenido vinculado a esta etiqueta ya no está disponible.",
      expiredOn: "Expiró el",
      comingSoon: "¡Próximamente!",
      comingSoonDesc: "Este contenido aún no está disponible. Se ha programado para más tarde.",
      available: "Disponible",
      emptyTag: "Nada aquí todavía",
      emptyTagDesc: "Esta etiqueta no tiene contenido configurado.",
    },
    
    // Upload Page
    upload: {
      title: "Subir contenido",
      subtitle: "Agrega mensajes, fotos y videos para crear tu recuerdo digital.",
      tagLocked: "Etiqueta bloqueada",
      tagLockedDesc: "Esta etiqueta ya tiene contenido. Resetéala primero.",
      backgroundColor: "Color de fondo",
      backgroundColorDesc: "Opcional - elige un color para tu página",
      expirationDate: "Fecha de expiración",
      expirationDateDesc: "Opcional - establece cuándo expirará este contenido",
      scheduleContent: "Programar contenido",
      scheduleContentDesc: "Opcional - establece cuándo será visible",
      scanNotifications: "Notificaciones de escaneo",
      notifyDesc: "Recibe notificaciones por email cuando alguien escanee esta etiqueta",
      titleLabel: "Título",
      titlePlaceholder: "ej., Feliz Cumpleaños, Felicitaciones...",
      messageLabel: "Mensaje",
      messagePlaceholder: "Escribe tu mensaje aquí...",
      generateWithAI: "Generar con IA",
      media: "Medios",
      uploadContent: "Sube tu contenido",
      photoGallery: "Galería de fotos",
      videoGallery: "Galería de videos",
      takePhoto: "Tomar foto",
      recordVideo: "Grabar video",
      audioFile: "Archivo de audio",
      audioMessage: "Mensaje de audio",
      dragToReorder: "Arrastra las fotos para reordenar. Toca para ver en pantalla completa.",
      previewCount: "Vista previa",
      saveContent: "Guardar contenido",
      saving: "Guardando...",
      preview: "Vista previa",
      getQRCode: "Obtener código QR",
      useTemplate: "Usar plantilla",
      expires: "Expira",
      goesLive: "Se activa",
      invalidTag: "Etiqueta inválida",
      invalidTagDesc: "Este código de etiqueta NFC no existe. Por favor escanea una etiqueta válida o contacta al proveedor.",
      aiWriter: "Escritor de mensajes IA",
      aiWriterDesc: "Agrega contexto opcional para ayudar a la IA a escribir un mensaje más personalizado para tu",
      aiContextPlaceholder: "ej., Para mi mejor amigo que ama la aventura...",
      generate: "Generar",
      generating: "Generando...",
      specialOccasion: "ocasión especial",
      templateApplied: "Plantilla aplicada",
      photosReordered: "Fotos reordenadas",
      mediaRemoved: "Medio eliminado",
      aiGenerated: "¡Mensaje IA generado!",
      qrDownloaded: "¡Código QR descargado!",
      contentSaved: "Contenido guardado exitosamente",
      compressing: "Comprimiendo imagen...",
      uploading: "Subiendo",
      processing: "Procesando video... (comprimiendo en servidor)",
      uploadingVideo: "Subiendo video...",
      videoOptimized: "El video será optimizado para reproducción rápida",
      compressed: "Comprimido",
      fileTooLarge: "El archivo excede el límite de 100MB",
      uploadFailed: "Error al subir archivo",
      uploadSuccess: "Archivo subido exitosamente",
      saveFailed: "Error al guardar contenido",
      aiFailed: "Error al generar mensaje",
      qrFailed: "Error al descargar código QR",
      compressionFailed: "No se pudo comprimir la imagen, subiendo original",
      // Drag & Drop
      dragDrop: "Arrastra y suelta archivos aquí",
      dropHere: "¡Suelta los archivos aquí!",
      dragDropDesc: "o usa los botones de abajo para seleccionar archivos",
      invalidFileType: "Por favor suelta archivos de imagen, video o audio",
      // Live Preview
      showLivePreview: "Vista en vivo",
      hideLivePreview: "Ocultar vista",
      livePreviewTitle: "Vista previa en vivo",
      livePreviewEmpty: "Comienza a agregar contenido para ver la vista previa",
      livePreviewHint: "Esta vista se actualiza mientras escribes",
    },
    
    // Admin
    admin: {
      dashboard: "Panel de administración",
      manageDesc: "Gestiona tus etiquetas NFC, genera UUIDs y crea códigos QR",
      totalTags: "Total de etiquetas",
      withContent: "Con contenido",
      totalViews: "Vistas totales",
      uniqueVisitors: "Visitantes únicos",
      viewsLast7Days: "Vistas - Últimos 7 días",
      allTags: "Todas las etiquetas",
      batches: "Lotes",
      uuidGenerator: "Generador UUID",
      customTag: "Etiqueta personalizada",
      bulkUpload: "Carga masiva",
      search: "Buscar...",
      selectAll: "Seleccionar todo",
      quickSelect: "Selección rápida",
      refresh: "Actualizar",
      whiteLabel: "Marca blanca",
    },
    
    // Templates
    templates: {
      title: "Plantillas de etiquetas",
      subtitle: "Comienza rápido con plantillas prediseñadas",
      wedding: "Boda",
      weddingDesc: "Comparte tu día especial con tus seres queridos",
      birthday: "Cumpleaños",
      birthdayDesc: "Celebra con un mensaje personalizado",
      business: "Tarjeta de negocios",
      businessDesc: "Información de contacto profesional",
      product: "Info de producto",
      productDesc: "Comparte detalles e instrucciones del producto",
      event: "Evento",
      eventDesc: "Detalles del evento e información RSVP",
      memorial: "Memorial",
      memorialDesc: "Honra y recuerda a tus seres queridos",
    },
  },
  
  fr: {
    // Common
    loading: "Chargement...",
    save: "Sauvegarder",
    cancel: "Annuler",
    delete: "Supprimer",
    edit: "Modifier",
    back: "Retour",
    next: "Suivant",
    done: "Terminé",
    error: "Erreur",
    success: "Succès",
    
    // View Page
    view: {
      shareTitle: "Partager",
      copyLink: "Copier le lien",
      linkCopied: "Lien copié!",
      invalidTag: "Tag invalide",
      invalidTagDesc: "Ce code de tag NFC n'existe pas. Veuillez scanner un tag valide.",
      expired: "Ce contenu a expiré",
      expiredDesc: "Le contenu lié à ce tag n'est plus disponible.",
      expiredOn: "Expiré le",
      comingSoon: "Bientôt disponible!",
      comingSoonDesc: "Ce contenu n'est pas encore disponible. Il est programmé pour plus tard.",
      available: "Disponible",
      emptyTag: "Rien ici pour l'instant",
      emptyTagDesc: "Ce tag n'a pas encore de contenu configuré.",
    },
    
    // Upload Page
    upload: {
      title: "Télécharger du contenu",
      subtitle: "Ajoutez des messages, photos et vidéos pour créer votre souvenir numérique.",
      tagLocked: "Tag verrouillé",
      tagLockedDesc: "Ce tag a déjà du contenu. Réinitialisez-le d'abord.",
      backgroundColor: "Couleur de fond",
      backgroundColorDesc: "Optionnel - choisissez une couleur pour votre page",
      expirationDate: "Date d'expiration",
      expirationDateDesc: "Optionnel - définir quand ce contenu doit expirer",
      scheduleContent: "Programmer le contenu",
      scheduleContentDesc: "Optionnel - définir quand ce contenu devient visible",
      scanNotifications: "Notifications de scan",
      notifyDesc: "Recevez un email quand quelqu'un scanne ce tag",
      titleLabel: "Titre",
      titlePlaceholder: "ex., Joyeux Anniversaire, Félicitations...",
      messageLabel: "Message",
      messagePlaceholder: "Écrivez votre message ici...",
      generateWithAI: "Générer avec l'IA",
      media: "Médias",
      uploadContent: "Téléchargez votre contenu",
      photoGallery: "Galerie photo",
      videoGallery: "Galerie vidéo",
      takePhoto: "Prendre une photo",
      recordVideo: "Enregistrer une vidéo",
      audioFile: "Fichier audio",
      audioMessage: "Message audio",
      dragToReorder: "Glissez les photos pour réorganiser. Cliquez pour voir en plein écran.",
      previewCount: "Aperçu",
      saveContent: "Sauvegarder",
      saving: "Sauvegarde...",
      preview: "Aperçu",
      getQRCode: "Obtenir le code QR",
      useTemplate: "Utiliser un modèle",
      expires: "Expire",
      goesLive: "Activé le",
      invalidTag: "Tag invalide",
      invalidTagDesc: "Ce code de tag NFC n'existe pas. Veuillez scanner un tag valide ou contacter le fournisseur.",
      aiWriter: "Rédacteur IA",
      aiWriterDesc: "Ajoutez du contexte optionnel pour aider l'IA à écrire un message plus personnalisé pour votre",
      aiContextPlaceholder: "ex., Pour mon meilleur ami qui aime l'aventure...",
      generate: "Générer",
      generating: "Génération...",
      specialOccasion: "occasion spéciale",
      templateApplied: "Modèle appliqué",
      photosReordered: "Photos réorganisées",
      mediaRemoved: "Média supprimé",
      aiGenerated: "Message IA généré!",
      qrDownloaded: "Code QR téléchargé!",
      contentSaved: "Contenu sauvegardé avec succès",
      compressing: "Compression de l'image...",
      uploading: "Téléchargement",
      processing: "Traitement de la vidéo... (compression sur le serveur)",
      uploadingVideo: "Téléchargement de la vidéo...",
      videoOptimized: "La vidéo sera optimisée pour une lecture rapide",
      compressed: "Compressé",
      fileTooLarge: "La taille du fichier dépasse 100Mo",
      uploadFailed: "Échec du téléchargement",
      uploadSuccess: "Fichier téléchargé avec succès",
      saveFailed: "Échec de la sauvegarde",
      aiFailed: "Échec de la génération du message",
      qrFailed: "Échec du téléchargement du code QR",
      compressionFailed: "Impossible de compresser l'image, téléchargement de l'original",
    },
    
    // Admin
    admin: {
      dashboard: "Tableau de bord",
      manageDesc: "Gérez vos tags NFC, générez des UUIDs et créez des codes QR",
      totalTags: "Total des tags",
      withContent: "Avec contenu",
      totalViews: "Vues totales",
      uniqueVisitors: "Visiteurs uniques",
      viewsLast7Days: "Vues - 7 derniers jours",
      allTags: "Tous les tags",
      batches: "Lots",
      uuidGenerator: "Générateur UUID",
      customTag: "Tag personnalisé",
      bulkUpload: "Import en masse",
      search: "Rechercher...",
      selectAll: "Tout sélectionner",
      quickSelect: "Sélection rapide",
      refresh: "Actualiser",
      whiteLabel: "Marque blanche",
    },
    
    // Templates
    templates: {
      title: "Modèles de tags",
      subtitle: "Démarrez rapidement avec des modèles prédéfinis",
      wedding: "Mariage",
      weddingDesc: "Partagez votre jour spécial avec vos proches",
      birthday: "Anniversaire",
      birthdayDesc: "Célébrez avec un message personnalisé",
      business: "Carte de visite",
      businessDesc: "Informations de contact professionnel",
      product: "Info produit",
      productDesc: "Partagez les détails et instructions du produit",
      event: "Événement",
      eventDesc: "Détails de l'événement et informations RSVP",
      memorial: "Mémorial",
      memorialDesc: "Honorez et souvenez-vous de vos proches",
    },
  },
  
  de: {
    // Common
    loading: "Laden...",
    save: "Speichern",
    cancel: "Abbrechen",
    delete: "Löschen",
    edit: "Bearbeiten",
    back: "Zurück",
    next: "Weiter",
    done: "Fertig",
    error: "Fehler",
    success: "Erfolg",
    
    // View Page
    view: {
      shareTitle: "Teilen",
      copyLink: "Link kopieren",
      linkCopied: "Link kopiert!",
      invalidTag: "Ungültiges Tag",
      invalidTagDesc: "Dieser NFC-Tag-Code existiert nicht. Bitte scannen Sie ein gültiges Tag.",
      expired: "Dieser Inhalt ist abgelaufen",
      expiredDesc: "Der mit diesem Tag verknüpfte Inhalt ist nicht mehr verfügbar.",
      expiredOn: "Abgelaufen am",
      comingSoon: "Bald verfügbar!",
      comingSoonDesc: "Dieser Inhalt ist noch nicht verfügbar. Er ist für später geplant.",
      available: "Verfügbar",
      emptyTag: "Noch nichts hier",
      emptyTagDesc: "Dieses Tag hat noch keinen Inhalt.",
    },
    
    // Upload Page
    upload: {
      title: "Inhalt hochladen",
      subtitle: "Fügen Sie Nachrichten, Fotos und Videos hinzu, um Ihre digitale Erinnerung zu erstellen.",
      tagLocked: "Tag gesperrt",
      tagLockedDesc: "Dieses Tag hat bereits Inhalt. Setzen Sie es zuerst zurück.",
      backgroundColor: "Hintergrundfarbe",
      backgroundColorDesc: "Optional - wählen Sie eine Farbe für Ihre Seite",
      expirationDate: "Ablaufdatum",
      expirationDateDesc: "Optional - legen Sie fest, wann dieser Inhalt ablaufen soll",
      scheduleContent: "Inhalt planen",
      scheduleContentDesc: "Optional - legen Sie fest, wann dieser Inhalt sichtbar wird",
      scanNotifications: "Scan-Benachrichtigungen",
      notifyDesc: "Erhalten Sie eine E-Mail, wenn jemand dieses Tag scannt",
      titleLabel: "Titel",
      titlePlaceholder: "z.B., Herzlichen Glückwunsch...",
      messageLabel: "Nachricht",
      messagePlaceholder: "Schreiben Sie Ihre Nachricht hier...",
      generateWithAI: "Mit KI generieren",
      media: "Medien",
      uploadContent: "Laden Sie Ihren Inhalt hoch",
      photoGallery: "Fotogalerie",
      videoGallery: "Videogalerie",
      takePhoto: "Foto aufnehmen",
      recordVideo: "Video aufnehmen",
      audioFile: "Audiodatei",
      audioMessage: "Sprachnachricht",
      dragToReorder: "Fotos ziehen zum Sortieren. Klicken für Vollbild.",
      previewCount: "Vorschau",
      saveContent: "Inhalt speichern",
      saving: "Speichern...",
      preview: "Vorschau",
      getQRCode: "QR-Code erhalten",
      useTemplate: "Vorlage verwenden",
      expires: "Läuft ab",
      goesLive: "Wird aktiv",
      invalidTag: "Ungültiges Tag",
      invalidTagDesc: "Dieser NFC-Tag-Code existiert nicht. Bitte scannen Sie ein gültiges Tag oder kontaktieren Sie den Anbieter.",
      aiWriter: "KI-Nachrichtenschreiber",
      aiWriterDesc: "Fügen Sie optionalen Kontext hinzu, um der KI zu helfen, eine persönlichere Nachricht für Ihre",
      aiContextPlaceholder: "z.B., Für meinen besten Freund, der Abenteuer liebt...",
      generate: "Generieren",
      generating: "Generieren...",
      specialOccasion: "besondere Gelegenheit",
      templateApplied: "Vorlage angewendet",
      photosReordered: "Fotos neu geordnet",
      mediaRemoved: "Medium entfernt",
      aiGenerated: "KI-Nachricht generiert!",
      qrDownloaded: "QR-Code heruntergeladen!",
      contentSaved: "Inhalt erfolgreich gespeichert",
      compressing: "Bild wird komprimiert...",
      uploading: "Hochladen",
      processing: "Video wird verarbeitet... (Komprimierung auf dem Server)",
      uploadingVideo: "Video wird hochgeladen...",
      videoOptimized: "Video wird für schnelle Wiedergabe optimiert",
      compressed: "Komprimiert",
      fileTooLarge: "Dateigröße überschreitet 100MB",
      uploadFailed: "Hochladen fehlgeschlagen",
      uploadSuccess: "Datei erfolgreich hochgeladen",
      saveFailed: "Speichern fehlgeschlagen",
      aiFailed: "Nachrichtengenerierung fehlgeschlagen",
      qrFailed: "QR-Code-Download fehlgeschlagen",
      compressionFailed: "Bild konnte nicht komprimiert werden, Original wird hochgeladen",
    },
    
    // Admin
    admin: {
      dashboard: "Admin-Dashboard",
      manageDesc: "Verwalten Sie Ihre NFC-Tags, generieren Sie UUIDs und erstellen Sie QR-Codes",
      totalTags: "Tags gesamt",
      withContent: "Mit Inhalt",
      totalViews: "Aufrufe gesamt",
      uniqueVisitors: "Eindeutige Besucher",
      viewsLast7Days: "Aufrufe - Letzte 7 Tage",
      allTags: "Alle Tags",
      batches: "Stapel",
      uuidGenerator: "UUID-Generator",
      customTag: "Benutzerdefiniertes Tag",
      bulkUpload: "Massenupload",
      search: "Suchen...",
      selectAll: "Alle auswählen",
      quickSelect: "Schnellauswahl",
      refresh: "Aktualisieren",
      whiteLabel: "White-Label",
    },
    
    // Templates
    templates: {
      title: "Tag-Vorlagen",
      subtitle: "Schnellstart mit vorgefertigten Vorlagen",
      wedding: "Hochzeit",
      weddingDesc: "Teilen Sie Ihren besonderen Tag mit Ihren Lieben",
      birthday: "Geburtstag",
      birthdayDesc: "Feiern Sie mit einer persönlichen Nachricht",
      business: "Visitenkarte",
      businessDesc: "Professionelle Kontaktinformationen",
      product: "Produktinfo",
      productDesc: "Teilen Sie Produktdetails und Anleitungen",
      event: "Veranstaltung",
      eventDesc: "Veranstaltungsdetails und RSVP-Informationen",
      memorial: "Gedenken",
      memorialDesc: "Ehren und erinnern Sie sich an Ihre Lieben",
    },
  },
  
  zh: {
    // Common
    loading: "加载中...",
    save: "保存",
    cancel: "取消",
    delete: "删除",
    edit: "编辑",
    back: "返回",
    next: "下一步",
    done: "完成",
    error: "错误",
    success: "成功",
    
    // View Page
    view: {
      shareTitle: "分享",
      copyLink: "复制链接",
      linkCopied: "链接已复制！",
      invalidTag: "无效标签",
      invalidTagDesc: "此NFC标签代码不存在。请扫描有效的标签。",
      expired: "内容已过期",
      expiredDesc: "与此标签关联的内容已不可用。",
      expiredOn: "过期于",
      comingSoon: "即将推出！",
      comingSoonDesc: "此内容尚不可用。已安排稍后发布。",
      available: "可用",
      emptyTag: "暂无内容",
      emptyTagDesc: "此标签尚未配置任何内容。",
    },
    
    // Upload Page
    upload: {
      title: "上传内容",
      subtitle: "添加消息、照片和视频，创建您的数字记忆。",
      tagLocked: "标签已锁定",
      tagLockedDesc: "此标签已有内容。请先重置。",
      backgroundColor: "背景颜色",
      backgroundColorDesc: "可选 - 为您的页面选择颜色",
      expirationDate: "过期日期",
      expirationDateDesc: "可选 - 设置此内容何时过期",
      scheduleContent: "定时发布",
      scheduleContentDesc: "可选 - 设置此内容何时可见",
      scanNotifications: "扫描通知",
      notifyDesc: "当有人扫描此标签时通过电子邮件通知您",
      titleLabel: "标题",
      titlePlaceholder: "例如：生日快乐，祝贺...",
      messageLabel: "消息",
      messagePlaceholder: "在这里写下您的消息...",
      generateWithAI: "AI生成",
      media: "媒体",
      uploadContent: "上传您的内容",
      photoGallery: "相册",
      videoGallery: "视频",
      takePhoto: "拍照",
      recordVideo: "录制视频",
      audioFile: "音频文件",
      audioMessage: "语音消息",
      dragToReorder: "拖动照片排序。点击查看全屏。",
      previewCount: "预览",
      saveContent: "保存内容",
      saving: "保存中...",
      preview: "预览",
      getQRCode: "获取二维码",
      useTemplate: "使用模板",
      expires: "过期",
      goesLive: "上线",
      invalidTag: "无效标签",
      invalidTagDesc: "此NFC标签代码不存在。请扫描有效的标签或联系提供商。",
      aiWriter: "AI消息助手",
      aiWriterDesc: "添加可选上下文，帮助AI为您的",
      aiContextPlaceholder: "例如：给我喜欢冒险的好朋友...",
      generate: "生成",
      generating: "生成中...",
      specialOccasion: "特殊场合",
      templateApplied: "模板已应用",
      photosReordered: "照片已重新排序",
      mediaRemoved: "媒体已删除",
      aiGenerated: "AI消息已生成！",
      qrDownloaded: "二维码已下载！",
      contentSaved: "内容保存成功",
      compressing: "正在压缩图片...",
      uploading: "上传中",
      processing: "正在处理视频...（服务器压缩中）",
      uploadingVideo: "正在上传视频...",
      videoOptimized: "视频将被优化以实现快速播放",
      compressed: "已压缩",
      fileTooLarge: "文件大小超过100MB限制",
      uploadFailed: "上传失败",
      uploadSuccess: "文件上传成功",
      saveFailed: "保存失败",
      aiFailed: "消息生成失败",
      qrFailed: "二维码下载失败",
      compressionFailed: "无法压缩图片，正在上传原图",
    },
    
    // Admin
    admin: {
      dashboard: "管理面板",
      manageDesc: "管理您的NFC标签，生成UUID和创建二维码",
      totalTags: "标签总数",
      withContent: "有内容",
      totalViews: "总浏览量",
      uniqueVisitors: "独立访客",
      viewsLast7Days: "浏览量 - 近7天",
      allTags: "所有标签",
      batches: "批次",
      uuidGenerator: "UUID生成器",
      customTag: "自定义标签",
      bulkUpload: "批量上传",
      search: "搜索...",
      selectAll: "全选",
      quickSelect: "快速选择",
      refresh: "刷新",
      whiteLabel: "白标",
    },
    
    // Templates
    templates: {
      title: "标签模板",
      subtitle: "使用预设模板快速开始",
      wedding: "婚礼",
      weddingDesc: "与亲人分享您的特别日子",
      birthday: "生日",
      birthdayDesc: "用个性化消息庆祝",
      business: "名片",
      businessDesc: "专业联系信息",
      product: "产品信息",
      productDesc: "分享产品详情和说明",
      event: "活动",
      eventDesc: "活动详情和回复信息",
      memorial: "纪念",
      memorialDesc: "纪念和缅怀亲人",
    },
  },
  
  ja: {
    // Common
    loading: "読み込み中...",
    save: "保存",
    cancel: "キャンセル",
    delete: "削除",
    edit: "編集",
    back: "戻る",
    next: "次へ",
    done: "完了",
    error: "エラー",
    success: "成功",
    
    // View Page
    view: {
      shareTitle: "共有",
      copyLink: "リンクをコピー",
      linkCopied: "リンクをコピーしました！",
      invalidTag: "無効なタグ",
      invalidTagDesc: "このNFCタグコードは存在しません。有効なタグをスキャンしてください。",
      expired: "このコンテンツは期限切れです",
      expiredDesc: "このタグにリンクされたコンテンツは利用できなくなりました。",
      expiredOn: "期限切れ日",
      comingSoon: "近日公開！",
      comingSoonDesc: "このコンテンツはまだ利用できません。後で公開される予定です。",
      available: "利用可能",
      emptyTag: "まだ何もありません",
      emptyTagDesc: "このタグにはまだコンテンツが設定されていません。",
    },
    
    // Upload Page
    upload: {
      title: "コンテンツをアップロード",
      subtitle: "メッセージ、写真、動画を追加して、デジタルメモリーを作成しましょう。",
      tagLocked: "タグがロックされています",
      tagLockedDesc: "このタグにはすでにコンテンツがあります。先にリセットしてください。",
      backgroundColor: "背景色",
      backgroundColorDesc: "オプション - ページの色を選択",
      expirationDate: "有効期限",
      expirationDateDesc: "オプション - このコンテンツの有効期限を設定",
      scheduleContent: "公開予約",
      scheduleContentDesc: "オプション - このコンテンツが表示される日時を設定",
      scanNotifications: "スキャン通知",
      notifyDesc: "誰かがこのタグをスキャンするとメールで通知します",
      titleLabel: "タイトル",
      titlePlaceholder: "例：お誕生日おめでとう...",
      messageLabel: "メッセージ",
      messagePlaceholder: "ここにメッセージを書いてください...",
      generateWithAI: "AIで生成",
      media: "メディア",
      uploadContent: "コンテンツをアップロード",
      photoGallery: "フォトギャラリー",
      videoGallery: "ビデオギャラリー",
      takePhoto: "写真を撮る",
      recordVideo: "ビデオを録画",
      audioFile: "オーディオファイル",
      audioMessage: "音声メッセージ",
      dragToReorder: "写真をドラッグして並べ替え。クリックでフルスクリーン表示。",
      previewCount: "プレビュー",
      saveContent: "コンテンツを保存",
      saving: "保存中...",
      preview: "プレビュー",
      getQRCode: "QRコードを取得",
      useTemplate: "テンプレートを使用",
      expires: "期限",
      goesLive: "公開",
      invalidTag: "無効なタグ",
      invalidTagDesc: "このNFCタグコードは存在しません。有効なタグをスキャンするか、プロバイダーにお問い合わせください。",
      aiWriter: "AIメッセージライター",
      aiWriterDesc: "AIがよりパーソナライズされたメッセージを書くために、オプションでコンテキストを追加してください。",
      aiContextPlaceholder: "例：冒険好きな親友へ...",
      generate: "生成",
      generating: "生成中...",
      specialOccasion: "特別な機会",
      templateApplied: "テンプレートが適用されました",
      photosReordered: "写真が並べ替えられました",
      mediaRemoved: "メディアが削除されました",
      aiGenerated: "AIメッセージが生成されました！",
      qrDownloaded: "QRコードがダウンロードされました！",
      contentSaved: "コンテンツが正常に保存されました",
      compressing: "画像を圧縮中...",
      uploading: "アップロード中",
      processing: "ビデオを処理中...（サーバーで圧縮中）",
      uploadingVideo: "ビデオをアップロード中...",
      videoOptimized: "ビデオは高速再生用に最適化されます",
      compressed: "圧縮済み",
      fileTooLarge: "ファイルサイズが100MBを超えています",
      uploadFailed: "アップロードに失敗しました",
      uploadSuccess: "ファイルが正常にアップロードされました",
      saveFailed: "保存に失敗しました",
      aiFailed: "メッセージの生成に失敗しました",
      qrFailed: "QRコードのダウンロードに失敗しました",
      compressionFailed: "画像を圧縮できませんでした。元の画像をアップロードします",
    },
    
    // Admin
    admin: {
      dashboard: "管理ダッシュボード",
      manageDesc: "NFCタグの管理、UUID生成、QRコード作成",
      totalTags: "タグ総数",
      withContent: "コンテンツあり",
      totalViews: "総閲覧数",
      uniqueVisitors: "ユニーク訪問者",
      viewsLast7Days: "閲覧数 - 過去7日間",
      allTags: "すべてのタグ",
      batches: "バッチ",
      uuidGenerator: "UUID生成",
      customTag: "カスタムタグ",
      bulkUpload: "一括アップロード",
      search: "検索...",
      selectAll: "すべて選択",
      quickSelect: "クイック選択",
      refresh: "更新",
      whiteLabel: "ホワイトラベル",
    },
    
    // Templates
    templates: {
      title: "タグテンプレート",
      subtitle: "プリセットテンプレートでクイックスタート",
      wedding: "結婚式",
      weddingDesc: "大切な日を愛する人と共有",
      birthday: "誕生日",
      birthdayDesc: "パーソナライズされたメッセージでお祝い",
      business: "名刺",
      businessDesc: "プロフェッショナルな連絡先情報",
      product: "製品情報",
      productDesc: "製品の詳細と説明を共有",
      event: "イベント",
      eventDesc: "イベント詳細とRSVP情報",
      memorial: "メモリアル",
      memorialDesc: "大切な人を敬い、偲ぶ",
    },
  },

  tr: {
    // Common
    loading: "Yükleniyor...",
    save: "Kaydet",
    cancel: "İptal",
    delete: "Sil",
    edit: "Düzenle",
    back: "Geri",
    next: "İleri",
    done: "Tamamlandı",
    error: "Hata",
    success: "Başarılı",
    
    // View Page
    view: {
      shareTitle: "Paylaş",
      copyLink: "Bağlantıyı Kopyala",
      linkCopied: "Bağlantı kopyalandı!",
      invalidTag: "Geçersiz Etiket",
      invalidTagDesc: "Bu NFC etiket kodu mevcut değil. Lütfen geçerli bir etiket tarayın.",
      expired: "Bu içeriğin süresi doldu",
      expiredDesc: "Bu etikete bağlı içerik artık mevcut değil.",
      expiredOn: "Sona erme tarihi",
      comingSoon: "Yakında!",
      comingSoonDesc: "Bu içerik henüz mevcut değil. Daha sonra yayınlanmak üzere planlandı.",
      available: "Mevcut",
      emptyTag: "Henüz içerik yok",
      emptyTagDesc: "Bu etiket henüz herhangi bir içerikle kurulmadı.",
    },
    
    // Upload Page
    upload: {
      title: "İçerik Yükle",
      subtitle: "Dijital anınızı oluşturmak için mesajlar, fotoğraflar ve videolar ekleyin.",
      tagLocked: "Etiket Kilitli",
      tagLockedDesc: "Bu etiketin zaten içeriği var. Yeni içerik eklemek için önce sıfırlayın.",
      backgroundColor: "Arka Plan Rengi",
      backgroundColorDesc: "İsteğe bağlı - sayfanız için bir renk seçin",
      expirationDate: "Son Kullanma Tarihi",
      expirationDateDesc: "İsteğe bağlı - bu içeriğin ne zaman sona ereceğini ayarlayın",
      scheduleContent: "İçerik Planla",
      scheduleContentDesc: "İsteğe bağlı - bu içeriğin ne zaman görünür olacağını ayarlayın",
      scanNotifications: "Tarama Bildirimleri",
      notifyDesc: "Birisi bu etiketi taradığında e-posta ile bildirim alın",
      titleLabel: "Başlık",
      titlePlaceholder: "örn., Mutlu Yıllar, Tebrikler...",
      messageLabel: "Mesaj",
      messagePlaceholder: "Mesajınızı buraya yazın...",
      generateWithAI: "AI ile Oluştur",
      media: "Medya",
      uploadContent: "İçeriğinizi yükleyin",
      photoGallery: "Fotoğraf Galerisi",
      videoGallery: "Video Galerisi",
      takePhoto: "Fotoğraf Çek",
      recordVideo: "Video Kaydet",
      audioFile: "Ses Dosyası",
      audioMessage: "Sesli Mesaj",
      dragToReorder: "Yeniden sıralamak için fotoğrafları sürükleyin. Tam ekran için tıklayın.",
      previewCount: "Önizleme",
      saveContent: "İçeriği Kaydet",
      saving: "Kaydediliyor...",
      preview: "Önizleme",
      getQRCode: "QR Kodu Al",
      useTemplate: "Şablon Kullan",
      expires: "Sona eriyor",
      goesLive: "Yayına giriyor",
      invalidTag: "Geçersiz Etiket",
      invalidTagDesc: "Bu NFC etiket kodu mevcut değil. Lütfen geçerli bir etiket tarayın veya sağlayıcıyla iletişime geçin.",
      aiWriter: "AI Mesaj Yazarı",
      aiWriterDesc: "AI'nın sizin için daha kişiselleştirilmiş bir mesaj yazmasına yardımcı olmak için isteğe bağlı bağlam ekleyin",
      aiContextPlaceholder: "örn., Macera seven en iyi arkadaşım için...",
      generate: "Oluştur",
      generating: "Oluşturuluyor...",
      specialOccasion: "özel gün",
      templateApplied: "Şablon uygulandı",
      photosReordered: "Fotoğraflar yeniden sıralandı",
      mediaRemoved: "Medya kaldırıldı",
      aiGenerated: "AI mesajı oluşturuldu!",
      qrDownloaded: "QR kodu indirildi!",
      contentSaved: "İçerik başarıyla kaydedildi",
      compressing: "Görsel sıkıştırılıyor...",
      uploading: "Yükleniyor",
      processing: "Video işleniyor... (sunucuda sıkıştırılıyor)",
      uploadingVideo: "Video yükleniyor...",
      videoOptimized: "Video hızlı oynatma için optimize edilecek",
      compressed: "Sıkıştırıldı",
      fileTooLarge: "Dosya boyutu 100MB sınırını aşıyor",
      uploadFailed: "Dosya yüklenemedi",
      uploadSuccess: "Dosya başarıyla yüklendi",
      saveFailed: "İçerik kaydedilemedi",
      aiFailed: "Mesaj oluşturulamadı",
      qrFailed: "QR kodu indirilemedi",
      compressionFailed: "Görsel sıkıştırılamadı, orijinal yükleniyor",
      // Drag & Drop
      dragDrop: "Dosyaları buraya sürükleyip bırakın",
      dropHere: "Dosyaları buraya bırakın!",
      dragDropDesc: "veya dosya seçmek için aşağıdaki düğmeleri kullanın",
      invalidFileType: "Lütfen görsel, video veya ses dosyaları bırakın",
      // Live Preview
      showLivePreview: "Canlı Önizleme",
      hideLivePreview: "Önizlemeyi Gizle",
      livePreviewTitle: "Canlı Önizleme",
      livePreviewEmpty: "Canlı önizleme görmek için içerik eklemeye başlayın",
      livePreviewHint: "Bu önizleme siz yazarken güncellenir",
    },
    
    // Admin
    admin: {
      dashboard: "Yönetim Paneli",
      manageDesc: "NFC etiketlerinizi yönetin, UUID'ler oluşturun ve QR kodları oluşturun",
      totalTags: "Toplam Etiket",
      withContent: "İçerikli",
      totalViews: "Toplam Görüntüleme",
      uniqueVisitors: "Tekil Ziyaretçi",
      viewsLast7Days: "Görüntüleme - Son 7 Gün",
      allTags: "Tüm Etiketler",
      batches: "Gruplar",
      uuidGenerator: "UUID Oluşturucu",
      customTag: "Özel Etiket",
      bulkUpload: "Toplu Yükleme",
      search: "Ara...",
      selectAll: "Tümünü seç",
      quickSelect: "Hızlı Seçim",
      refresh: "Yenile",
      whiteLabel: "Beyaz Etiket",
    },
    
    // Templates
    templates: {
      title: "Etiket Şablonları",
      subtitle: "Önceden tasarlanmış şablonlarla hızlı başlangıç",
      wedding: "Düğün",
      weddingDesc: "Özel gününüzü sevdiklerinizle paylaşın",
      birthday: "Doğum Günü",
      birthdayDesc: "Kişiselleştirilmiş bir mesajla kutlayın",
      business: "Kartvizit",
      businessDesc: "Profesyonel iletişim bilgileri",
      product: "Ürün Bilgisi",
      productDesc: "Ürün detaylarını ve talimatları paylaşın",
      event: "Etkinlik",
      eventDesc: "Etkinlik detayları ve RSVP bilgileri",
      memorial: "Anma",
      memorialDesc: "Sevdiklerinizi onurlandırın ve anın",
    },

    // Marketing - Homepage
    marketing: {
      nav: {
        features: "Özellikler",
        solutions: "Çözümler",
        pricing: "Fiyatlandırma",
        about: "Hakkımızda",
        faq: "SSS",
        contact: "İletişim",
        login: "Giriş",
        getStarted: "Başla",
        language: "Dil",
      },
      hero: {
        badge: "Fiziksel'den Dijital'e Köprü",
        title: "Herhangi Bir Nesneyi",
        titleHighlight: "Etkileşimli Deneyime",
        titleEnd: "Dönüştürün",
        subtitle: "Fiziksel ürünleri dijital içeriğe bağlayan NFC etiketleri ve QR kodları. Etkileşimi takip edin, içeriği anında güncelleyin ve unutulmaz anlar yaratın.",
        requestDemo: "Demo Talep Et",
        seeHowItWorks: "Nasıl Çalışır",
        trustedBy: "500+ işletme tarafından güveniliyor",
      },
      trustedBrands: "ÖNDE GELEN MARKALAR TARAFINDAN GÜVENİLİYOR",
      features: {
        title: "Bağlantı İçin İhtiyacınız Olan Her Şey",
        subtitle: "NFC ve QR deneyimlerinizi oluşturmak, yönetmek ve izlemek için güçlü araçlar",
        dualTech: "Çift Teknoloji",
        dualTechDesc: "Her etiket hem NFC hem de QR kod alır. Maksimum erişim için birlikte veya ayrı kullanın.",
        instantUpdates: "Anında Güncellemeler",
        instantUpdatesDesc: "Yeniden basmadan içeriği istediğiniz zaman değiştirin. Yeni kampanyalar için sıfırlayın ve yeniden kullanın.",
        realTimeAnalytics: "Gerçek Zamanlı Analitik",
        realTimeAnalyticsDesc: "Konum, cihaz ve zaman verileriyle her taramayı izleyin. Kitlenizi tanıyın.",
        whiteLabel: "Beyaz Etiket Hazır",
        whiteLabelDesc: "Sizin markanız, sizin platformunuz. Ajanslar ve kurumlar için tamamen özelleştirilebilir.",
        multiLanguage: "Çok Dilli",
        multiLanguageDesc: "7+ dil desteği. Küresel kitlelere zahmetsizce ulaşın.",
        aiPowered: "AI Destekli İçerik",
        aiPoweredDesc: "AI'nın ilgi çekici mesajlar yazmasına izin verin. Saniyeler içinde etkileyici içerik oluşturun.",
      },
      solutions: {
        title: "Her İhtiyaç İçin Çözümler",
        subtitle: "İki güçlü platform, sonsuz olasılıklar",
        forBusiness: "İşletmeler İçin",
        forBusinessDesc: "Analitik, toplu işlemler ve beyaz etiket özellikleriyle kurumsal düzeyde NFC & QR yönetim platformu.",
        businessFeatures: [
          "Binlerce etiketi ölçeklenebilir şekilde yönetin",
          "Gerçek zamanlı analitik paneli",
          "Markanız için beyaz etiket",
          "API erişimi ve entegrasyonlar",
          "Çok kiracılı mimari"
        ],
        perfectFor: "İdeal:",
        businessTags: ["Pazarlama Ajansları", "Perakende Markaları", "Etkinlik Şirketleri", "Üreticiler"],
        giftsAwards: "Hediyeler & Ödüller",
        giftsAwardsDesc: "Hediyeleri ve kupaları sihirli hatıralara dönüştürün. Video mesajları, anılar ve sonsuza kadar süren anlar.",
        giftFeatures: [
          "Videolu akıllı tebrik kartları",
          "Zafer anlarını yeniden oynatan kupalar",
          "Anılarla düğün hediyeleri",
          "Mesajlarla kurumsal ödüller",
          "Anma hediyeleri"
        ],
        viewProducts: "Ürünleri ve Fiyatları Görüntüle",
      },
      howItWorks: {
        title: "Nasıl Çalışır",
        subtitle: "Etkileşimli deneyimler oluşturmak için üç basit adım",
        step1: "Etiketinizi Oluşturun",
        step1Desc: "Saniyeler içinde benzersiz NFC etiketleri ve QR kodları oluşturun. Renkleri ve markayı özelleştirin.",
        step2: "İçerik Ekleyin",
        step2Desc: "Fotoğraflar, videolar ve mesajlar yükleyin. Etkileyici içerik yazmak için AI'dan yardım alın.",
        step3: "Takip Edin ve Etkileşim Kurun",
        step3Desc: "Taramaları gerçek zamanlı izleyin. Analitikleri, konumları ve etkileşim metriklerini görün.",
      },
      pricing: {
        title: "Basit, Şeffaf Fiyatlandırma",
        subtitle: "Ücretsiz başlayın, büyüdükçe ölçeklendirin",
        starter: "Başlangıç",
        starterDesc: "Küçük işletmeler için mükemmel",
        starterFeatures: ["100 etikete kadar", "Temel analitik", "QR kod oluşturucu", "E-posta desteği"],
        professional: "Profesyonel",
        professionalDesc: "Büyüyen işletmeler için",
        professionalFeatures: ["1.000 etikete kadar", "Gelişmiş analitik", "Özel QR markalama", "API erişimi", "Öncelikli destek", "Takım işbirliği"],
        enterprise: "Kurumsal",
        enterpriseDesc: "Büyük organizasyonlar için",
        enterpriseFeatures: ["Sınırsız etiket", "Beyaz etiket platformu", "Özel destek", "Özel entegrasyonlar", "SLA garantisi", "Kurulum yardımı"],
        mostPopular: "En Popüler",
        startFreeTrial: "Ücretsiz Deneme Başlat",
        contactSales: "Satışla İletişim",
        custom: "Özel",
        month: "/ay",
      },
      testimonials: {
        title: "İşletmeler Tarafından Seviliyor",
        testimonial1: "ScanIt2Go müşterilerle etkileşim şeklimizi dönüştürdü. Analitikler tek başına perakende stratejimiz için oyun değiştirici oldu.",
        testimonial1Author: "Sarah Johnson",
        testimonial1Role: "Pazarlama Direktörü",
        testimonial1Company: "Fashion Retail Co",
        testimonial2: "Ödüllerimize NFC etiketleri ekledik. Kazananlar artık zafer anlarını istedikleri zaman yeniden yaşayabilir. Sihirli.",
        testimonial2Author: "Michael Chen",
        testimonial2Role: "Etkinlik Yöneticisi",
        testimonial2Company: "Sports Awards Inc",
        testimonial3: "Beyaz etiket özelliği bunu müşterilerimize kendi markamız altında sunmamızı sağladı. Beklemediğimiz gelir akışı!",
        testimonial3Author: "Emma Williams",
        testimonial3Role: "Ajans Sahibi",
        testimonial3Company: "Digital First Agency",
      },
      cta: {
        title: "Başlamaya Hazır mısınız?",
        subtitle: "ScanIt2Go ile etkileşimli deneyimler yaratan 500+ işletmeye katılın",
        requestDemo: "Demo Talep Et",
        viewPricing: "Fiyatlandırmayı Görüntüle",
      },
      contact: {
        title: "İletişime Geçin",
        subtitle: "Fiziksel ürünlerinizi etkileşimli deneyimlere dönüştürmeye hazır mısınız? İhtiyaçlarınız hakkında konuşalım.",
        website: "Web Sitesi",
        email: "E-posta",
        phone: "Telefon",
        firstName: "Ad",
        lastName: "Soyad",
        emailLabel: "E-posta",
        company: "Şirket",
        message: "Mesaj",
        messagePlaceholder: "Projeniz hakkında bize bilgi verin...",
        sendMessage: "Mesaj Gönder",
        sending: "Gönderiliyor...",
        captcha: "Güvenlik Kontrolü",
        newQuestion: "Yeni soru",
      },
      footer: {
        tagline: "Fiziksel'den dijitale köprü. Unutulmaz deneyimler yaratın.",
        product: "Ürün",
        features: "Özellikler",
        pricing: "Fiyatlandırma",
        solutions: "Çözümler",
        dashboard: "Panel",
        company: "Şirket",
        aboutUs: "Hakkımızda",
        faq: "SSS",
        contact: "İletişim",
        privacyPolicy: "Gizlilik Politikası",
        termsOfService: "Hizmet Şartları",
        allRightsReserved: "Tüm hakları saklıdır.",
      },
    },

    // About Page
    about: {
      badge: "Hikayemiz",
      title: "Fiziksel ve Dijital Dünyaları Birleştiriyoruz",
      subtitle: "Basit bir fikirle başladık: fiziksel nesneler hikayeler anlatmalı. Bugün işletmelerin ve bireylerin anlamlı bağlantılar kurmasına yardımcı oluyoruz.",
      ourMission: "Misyonumuz",
      missionText: "Gündelik nesnelerin güçlü iletişim araçlarına dönüşebileceğine inanıyoruz. Misyonumuz, fiziksel ürünleri dijital deneyimlerle birbirine bağlayan sezgisel, güvenilir ve güzel teknoloji sağlamaktır.",
      stats: {
        tagsCreated: "Etiket Oluşturuldu",
        businessesServed: "Hizmet Verilen İşletme",
        countriesReached: "Ulaşılan Ülke",
        uptime: "Çalışma Süresi",
      },
      ourVision: "Vizyonumuz",
      visionText: "Fiziksel ve dijital arasındaki çizginin kaybolduğu bir gelecek hayal ediyoruz. Her ürün bir hikaye anlatabileceği, her hediye benzersiz bir deneyim taşıyabileceği ve her işletmenin müşterileriyle anlamlı şekillerde bağlantı kurabileceği bir gelecek.",
      visionPoints: [
        "Küresel erişim için evrensel NFC & QR standartları",
        "Tüm sektörler için kurumsal düzey güvenlik",
        "Maksimum erişilebilirlik için basitleştirilmiş kullanıcı deneyimi",
        "Sürekli ürün iyileştirmesi için sürdürülebilir inovasyon"
      ],
      ourValues: "Değerlerimiz",
      values: {
        innovation: "İnovasyon",
        innovationDesc: "Sürekli olarak teknolojinin sınırlarını zorluyoruz.",
        customerFocus: "Müşteri Odaklılık",
        customerFocusDesc: "Her karar müşteri ihtiyaçları göz önünde bulundurularak alınır.",
        passion: "Tutku",
        passionDesc: "Teknolojinin hayatları birbirine bağlama gücüne inanıyoruz.",
        simplicity: "Sadelik",
        simplicityDesc: "Karmaşık teknolojiyi herkes için erişilebilir kılıyoruz.",
      },
      ourJourney: "Yolculuğumuz",
      journey: {
        year2023: "2023",
        event2023: "Kuruluş",
        desc2023: "İngiltere'de küçük bir ekiple yolculuğumuz başladı.",
        year2024: "2024",
        event2024: "Ürün Lansmanı",
        desc2024: "İlk işletme müşterilerimize hizmet vererek platformu resmi olarak başlattık.",
        year2025: "2025",
        event2025: "Küresel Genişleme",
        desc2025: "Dünyanın her yerinden 500+ işletmeye hizmet verecek şekilde genişledik.",
      },
      team: {
        title: "Ekibimizle Tanışın",
        subtitle: "ScanIt2Go'yu mümkün kılan tutkulu insanlar",
        founder: "Kurucu & CEO",
        founderDesc: "15+ yıllık teknoloji ve pazarlama deneyimine sahip vizyon sahibi.",
      },
      ctaTitle: "Hikayemizin Parçası Olun",
      ctaSubtitle: "Bağlantılı deneyimlerin geleceğini şekillendirmemize yardım eden binlerce işletme ve bireye katılın.",
      getStarted: "Bugün Başlayın",
      contactUs: "Bizimle İletişime Geçin",
    },

    // FAQ Page
    faq: {
      badge: "Yardım Merkezi",
      title: "Sıkça Sorulan Sorular",
      subtitle: "ScanIt2Go hakkında bilmeniz gereken her şey. Aradığınızı bulamıyor musunuz? Ekibimizle iletişime geçin.",
      searchPlaceholder: "Soru ara...",
      categories: {
        all: "Tümü",
        general: "Genel",
        technical: "Teknik",
        pricing: "Fiyatlandırma & Planlar",
        business: "İş Çözümleri",
        gifts: "Hediyeler & Ödüller",
      },
      questions: {
        q1: "ScanIt2Go nedir?",
        a1: "ScanIt2Go, fiziksel ürünleri dijital içeriğe bağlayan bir NFC ve QR kod yönetim platformudur. Akıllı etiketler oluşturmanıza, içerik yönetmenize, etkileşimi izlemenize ve bağlı deneyimler oluşturmanıza olanak tanır.",
        q2: "NFC ve QR kodları nasıl çalışır?",
        a2: "NFC (Yakın Alan İletişimi) etiketleri bir akıllı telefon yaklaştırıldığında tetiklenir, QR kodları ise kamerayla taranır. Her iki teknoloji de kullanıcıları dijital içeriğe yönlendirir. Maksimum uyumluluk için hem NFC hem de QR sağlıyoruz.",
        q3: "Herhangi bir akıllı telefonla çalışır mı?",
        a3: "QR kodları herhangi bir kameralı akıllı telefonla çalışır. NFC, çoğu modern akıllı telefon tarafından desteklenir (iPhone 7+, çoğu Android cihaz). Daha geniş uyumluluk için hem NFC hem de QR seçenekleri sağlıyoruz.",
        q4: "İçeriği yayınladıktan sonra değiştirebilir miyim?",
        a4: "Evet! En büyük avantajlarımızdan biri budur. İçeriği istediğiniz zaman fiziksel etiketi değiştirmeden güncelleyebilirsiniz. Promosyonlar, mevsimsel mesajlar veya sürekli kampanyalar için mükemmel.",
        q5: "İçerik güvenli mi?",
        a5: "Kesinlikle. Güvenli barındırma, kurumsal altyapı ve GDPR uyumlu veri işleme kullanıyoruz. Kurumsal müşteriler için ek güvenlik özellikleri mevcuttur.",
        q6: "Ne tür içerik yükleyebilirim?",
        a6: "Metin, görsel, video ve ses dosyaları yükleyebilirsiniz. Platformumuz çoğu yaygın formatı destekler ve multimedya içeriği için dosya başına 100MB'a kadar izin verir.",
        q7: "Fiyatlandırmanız nasıl çalışır?",
        a7: "Farklı ihtiyaçlara yönelik katmanlı planlar sunuyoruz: Başlangıç (100 etikete kadar), Profesyonel (1.000 etikete kadar) ve Kurumsal (sınırsız). Her plan analitik, QR oluşturma ve destek içerir.",
        q8: "Ücretsiz deneme var mı?",
        a8: "Evet! Tüm ücretli planlar, tam özellik erişimiyle 14 günlük ücretsiz deneme sunuyor. Kredi kartı gerekmez.",
        q9: "Özel fiyatlandırma alabilir miyim?",
        a9: "Kesinlikle. Kurumsal müşteriler ve büyük hacimli ihtiyaçlar için benzersiz gereksinimlerinize göre özel fiyatlandırma sunuyoruz. Satış ekibimizle iletişime geçin.",
        q10: "Beyaz etiketleme nedir?",
        a10: "Beyaz etiketleme, kendi markalama, renkler ve logonuzla platformumuzu sunmanıza olanak tanır. Kendi NFC/QR hizmetlerini sunmak isteyen ajanslar ve işletmeler için mükemmel.",
        q11: "Müşterilerim için hesaplar oluşturabilir miyim?",
        a11: "Evet! Çok kiracılı sistemimiz alt hesaplar oluşturmanıza, izinleri yönetmenize ve her müşteri için ayrı faturalandırmayı takip etmenize olanak tanır.",
        q12: "Ne tür analitikler izliyorsunuz?",
        a12: "Tarama sayılarını, benzersiz ziyaretçileri, coğrafi konumu, cihaz türlerini, tarayıcı bilgilerini ve zaman damgalarını izliyoruz. İşletmeler hedef kitlerini gerçekten anlayabilir.",
        q13: "Hediye ürünleri nasıl çalışır?",
        a13: "Hediye ürünlerimiz (kartlar, ödüller, kupalar) gömülü NFC etiketleri ile gelir. Bir video veya mesaj yüklersiniz, alıcı hediyeyi taradığında içeriğinizi görür. Özel kutlamalar için mükemmel.",
        q14: "Hediyeler için hangi ürünler mevcut?",
        a14: "NFC özellikli tebrik kartları, kristal ödüller, kupalar, plaketler, anahtarlıklar ve çeşitli aksesuarlar sunuyoruz. Tüm ürünler belirli baskı tasarımlarıyla özelleştirilebilir.",
        q15: "Hediye içeriğini önceden ayarlayabilir miyim?",
        a15: "Evet! İçerik planlamayı destekliyoruz. Belirli bir tarih/saat için içerik ayarlayın - etkinlik, kutlama veya duyuru için canlıya geçecektir.",
        q16: "Hediye mesajım ne kadar süre kalır?",
        a16: "Hediyelere bağlı içerik kalıcıdır. Kaldırmayı seçmedikçe sonsuza kadar erişilebilir kalır. İsteğe bağlı süre sonu tarihleri de ayarlayabilirsiniz.",
        q17: "Birisi hediyemi görüntülediğinde bilgilendirilecek miyim?",
        a17: "Evet! İsteğe bağlı bildirimler hediyeniz ilk görüntülendiğinde size uyarı gönderir - mesajınızın ne zaman deneyimlendiğini bilirsiniz.",
        q18: "Toplu siparişler için indirim var mı?",
        a18: "Kesinlikle! 10+ birim, 50+ birim ve 100+ birim siparişler için hacim indirimleri sunuyoruz. Özel fiyatlandırma için satış ekibimizle iletişime geçin.",
      },
      stillHaveQuestions: "Hala Sorularınız mı Var?",
      stillHaveQuestionsDesc: "Aradığınızı bulamadınız mı? Ekibimiz size yardımcı olmaktan mutluluk duyacaktır.",
      emailUs: "Bize E-posta Gönderin",
      contactSupport: "Destekle İletişim",
    },

    // Gifts Pricing Page
    giftsPricing: {
      badge: "Hediyeler & Ödüller",
      title: "Hediyelerinizi Unutulmaz Kılın",
      subtitle: "Video mesajları, fotoğraflar ve anılarla hayat bulan özel ürünler. Basit ve güzel.",
      howItWorks: "Nasıl Çalışır",
      step1: "Ürününüzü Seçin",
      step1Desc: "NFC ve QR özellikli ürünler arasından seçim yapın",
      step2: "Mesajınızı Yükleyin",
      step2Desc: "Özel yükleme sayfanız aracılığıyla video, fotoğraf veya metin ekleyin",
      step3: "Paylaşın & Şaşırtın",
      step3Desc: "Alıcı bir dokunuş veya tarama ile anlarınızı deneyimler",
      products: {
        title: "Ürünlerimiz",
        subtitle: "Her ürün gömülü NFC etiketleri ve QR kodlarıyla birlikte gelir",
        greetingCards: "Akıllı Tebrik Kartları",
        greetingCardsDesc: "Video mesajları ve fotoğraflarla hayat bulan güzel kartlar. Doğum günleri, düğünler, mezuniyetler ve özel günler için mükemmel.",
        greetingCardsFeature1: "Gömülü NFC etiket + QR kod",
        greetingCardsFeature2: "Premium kart stoğu",
        greetingCardsFeature3: "Video ve fotoğrafları destekler",
        greetingCardsFeature4: "Sınırsız içerik güncellemesi",
        singleCard: "Tekli Kart",
        pack3: "3'lü Paket",
        pack10: "10'lu Paket",
        metalSurface: "metal yüzey için +$1",
        awards: "Kristal Ödüller",
        awardsDesc: "Gömülü video anılarıyla zarif kristal ödüller. Zafer anlarını yeniden yaşatan şirket takdirleri, spor başarıları ve özel tanımalar için idealdir.",
        awardsFeature1: "Premium kristal malzeme",
        awardsFeature2: "Gömülü NFC + QR",
        awardsFeature3: "Özel gravür seçenekleri",
        awardsFeature4: "Lüks sunum kutusu dahil",
        small: "Küçük (10cm)",
        medium: "Orta (15cm)",
        large: "Büyük (20cm)",
        trophies: "Video Kupalar",
        trophiesDesc: "Tıpkı taç gibi, ama anılarla. Takımlar ve başarılar için mükemmel.",
        trophiesFeature1: "Dayanıklı metal yapı",
        trophiesFeature2: "Tabanda NFC etiket",
        trophiesFeature3: "Özel gravür mevcuttur",
        trophiesFeature4: "Tüm popüler boyutlar",
        plaques: "Anma Plaketleri",
        plaquesDesc: "Sevdiklerinizi videolar, fotoğraflar ve paylaşılan anılarla onurlandırın. Hassas malzemelerle yapılmış kalıcı hatıralar.",
        plaquesFeature1: "Hava koşullarına dayanıklı malzemeler",
        plaquesFeature2: "Gömülü NFC teknolojisi",
        plaquesFeature3: "Fotoğraf gravürü mevcuttur",
        plaquesFeature4: "İç ve dış mekan kullanımı",
        memorialSmall: "Anma Küçük",
        memorialLarge: "Anma Büyük",
      },
      bulkOrders: {
        title: "Toplu Siparişler & Özelleştirme",
        subtitle: "İşletmeler ve etkinlikler için indirimli fiyatlar",
        weddingFavors: "Düğün Hediyeleri",
        weddingFavorsDesc: "Misafirler için kişiselleştirilmiş NFC kartları. Her biri benzersiz içeriğe bağlantı verebilir.",
        corporateGifts: "Kurumsal Hediyeler",
        corporateGiftsDesc: "Video mesajlarıyla markalı ödüller ve tanıma hediyeleri.",
        eventMemorabilia: "Etkinlik Hatıraları",
        eventMemorabilitaDesc: "Konserler, konferanslar ve özel etkinlikler için NFC özellikli ürünler.",
        contactForPricing: "Fiyatlandırma için İletişim",
      },
      orderNow: "Şimdi Sipariş Ver",
      contactSales: "Satışla İletişim",
      questions: "Sorularınız mı var?",
      questionsText: "Ekibimiz yardımcı olmaktan mutluluk duyar. Toplu siparişler ve özel gereksinimler için bize ulaşın.",
      emailUs: "Bize E-posta Gönderin",
    },
  },
};

// Available languages
export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
];

// Get translation helper
export const getTranslation = (lang, path) => {
  const keys = path.split('.');
  let result = translations[lang] || translations.en;
  
  for (const key of keys) {
    result = result?.[key];
    if (result === undefined) {
      // Fallback to English
      result = translations.en;
      for (const k of keys) {
        result = result?.[k];
      }
      break;
    }
  }
  
  return result || path;
};

// Language context hook
export const getStoredLanguage = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('app_language') || 'en';
  }
  return 'en';
};

export const setStoredLanguage = (lang) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('app_language', lang);
  }
};

export default translations;
